import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { eventGroupsApi, type EventGroupRow } from '@/api/eventGroups';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import {
  ResourceDataGrid, type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';

import { EventGroupDialog } from './EventGroupDialog';

export function EventGroupsGrid() {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_event_group_manage');

  const [editId, setEditId] = useState<number | null | 'new'>(null);
  useCreateIntent(() => setEditId('new'));
  const [deleteRow, setDeleteRow] = useState<EventGroupRow | null>(null);

  function queryFn(params: ResourceDataGridParams) {
    return eventGroupsApi.list({
      page: params.page,
      page_size: params.pageSize,
      ordering: params.ordering,
      search: params.search,
    });
  }

  const refresh = () => qc.invalidateQueries({ queryKey: ['consoleEventGroups'] });

  const doDelete = () => {
    if (!deleteRow) return;
    eventGroupsApi
      .remove(deleteRow.id)
      .then(() => { snackbar.show('Event group deleted', 'success'); setDeleteRow(null); refresh(); })
      .catch((e: unknown) =>
        snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed', 'error'));
  };

  const columns = useMemo<GridColDef<EventGroupRow>[]>(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, filterable: false },
      { field: 'description', headerName: 'Description', flex: 1.4, sortable: false, filterable: false },
      {
        field: 'event_codes', headerName: 'Codes', width: 90, sortable: false, filterable: false,
        valueGetter: (_v, row) => row.event_codes.length,
      },
      { field: 'updated_ts', headerName: 'Updated', width: 160, filterable: false,
        valueGetter: (_v, row) => (row.updated_ts ? row.updated_ts.replace('T', ' ').slice(0, 16) : '') },
      {
        field: 'actions', headerName: '', width: 90, sortable: false, filterable: false,
        renderCell: (p) => (
          <Stack direction="row">
            <Tooltip title="Edit">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditId(p.row.id); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canManage && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteRow(p.row); }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [canManage],
  );

  return (
    <Box>
      <Stack direction="row" sx={{ mb: 2 }} justifyContent="flex-end">
        <PermissionGate perm="console_event_group_manage">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditId('new')}>New group</Button>
        </PermissionGate>
      </Stack>

      <ResourceDataGrid<EventGroupRow>
        queryKey={['consoleEventGroups']}
        queryFn={queryFn}
        columns={columns}
        batchResource="event_groups"
        batchActions={[deleteBatchAction('console_event_group_manage')]}
        defaultSort={{ field: 'name', sort: 'asc' }}
        onRowClick={(id) => setEditId(id as number)}
        headerToolbar={{ search: true, searchPlaceholder: 'Search groups', exportFileName: 'event-groups' }}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
      />

      {editId !== null && (
        <EventGroupDialog
          groupId={editId === 'new' ? null : editId}
          onClose={() => setEditId(null)}
          onSaved={() => { setEditId(null); refresh(); }}
        />
      )}

      <ConfirmDialog
        open={deleteRow !== null}
        title="Delete event group?"
        message={`Delete "${deleteRow?.name ?? ''}"? Blacklist rules referencing it will lose this group.`}
        confirmLabel="Delete"
        destructive
        onConfirm={doDelete}
        onCancel={() => setDeleteRow(null)}
      />
    </Box>
  );
}
